import { useState } from "react";
import { X, Loader2, Globe } from "lucide-react";
import { api } from "../lib/api";

// PC web browser — minimalist single-input "address bar" + result panel.
// Backend reachability is checked by issuing a `ping <ip>` against the PC's
// own console session (re-uses the existing reachability simulator) and
// translating the outcome into HTTP terms the way Packet Tracer does.
export default function PcBrowserModal({ open, onClose, sessionId, deviceId, deviceLabel }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {ok: bool, target: str, message: str}

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setResult(null);
    const target = url.trim().replace(/^https?:\/\//, "").split("/")[0].split(":")[0];
    if (!target) return;
    setLoading(true);
    try {
      // Use the existing per-device command endpoint to ask the PC to ping.
      // The simulator's reachability logic answers Reply / Request timed out.
      const { data } = await api.post(`/labs/session/${sessionId}/command`, {
        command: `ping ${target}`,
        device_id: deviceId,
      });
      const ok = (data?.output || "").includes("Reply from");
      setResult({
        ok,
        target,
        message: ok
          ? `Connected · HTTP 200 OK · ${target}`
          : `Failed · No route to host · ${target}`,
      });
    } catch (err) {
      setResult({
        ok: false,
        target,
        message: `Failed · ${err?.response?.data?.detail || "browser error"}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setUrl("");
    setResult(null);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pc-browser-modal">
      <div className="bg-[#141416] border border-zinc-800 max-w-lg w-full">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Globe size={16} className="text-[#00E5FF]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono">
                web browser
              </div>
              <div className="font-display font-bold text-base mt-0.5">{deviceLabel || deviceId}</div>
            </div>
          </div>
          <button onClick={close} className="p-1 text-zinc-500 hover:text-white" data-testid="pc-browser-close">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500 mb-1.5">
              address
            </span>
            <input
              autoFocus
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="http://10.0.0.1"
              className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
              data-testid="pc-browser-url"
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
            />
          </label>
          <button
            type="submit"
            disabled={loading || !url.trim()}
            className="w-full px-3 py-2 bg-[#00E5FF] text-black font-semibold flex items-center justify-center gap-2 text-xs uppercase tracking-widest disabled:opacity-50"
            data-testid="pc-browser-go"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Globe size={12} />} go
          </button>

          {result && (
            <div
              className={`mt-2 p-3 border font-mono text-sm ${
                result.ok
                  ? "border-[#00FF41] text-[#00FF41] bg-[#00FF41]/10"
                  : "border-red-500/60 text-red-400 bg-red-500/10"
              }`}
              data-testid="pc-browser-result"
            >
              {result.message}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
